import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { format } from 'date-fns';
import { 
  Calendar, Clock, Image, Link, Send, X, Hash,
  Facebook, Twitter, Linkedin, Instagram, RefreshCw,
  Share, ExternalLink, Plus, Check, Youtube, Video,
  Radio, Globe, MapPin, Cloud, Camera, PinIcon
} from 'lucide-react';
import toast from 'react-hot-toast';
import NewsFeed from './NewsFeed';

interface MediaFile {
  file: File;
  preview: string;
  type: 'image' | 'video';
}

interface Platform {
  id: string;
  name: string;
  icon: React.ReactNode;
  characterLimit?: number;
}

interface NewsItem {
  title: string;
  link: string;
  pubDate: string;
  description: string;
  source: string;
  category: string;
}

const platforms: Platform[] = [
  { id: 'facebook', name: 'Facebook', icon: <Facebook className="h-5 w-5" />, characterLimit: 63206 },
  { id: 'twitter', name: 'Twitter/X', icon: <Twitter className="h-5 w-5" />, characterLimit: 280 },
  { id: 'instagram', name: 'Instagram', icon: <Instagram className="h-5 w-5" />, characterLimit: 2200 },
  { id: 'linkedin', name: 'LinkedIn', icon: <Linkedin className="h-5 w-5" />, characterLimit: 3000 },
  { id: 'youtube', name: 'YouTube', icon: <Youtube className="h-5 w-5" /> },
  { id: 'tiktok', name: 'TikTok', icon: <Video className="h-5 w-5" /> },
  { id: 'rumble', name: 'Rumble', icon: <Radio className="h-5 w-5" /> },
  { id: 'truthsocial', name: 'Truth Social', icon: <Share className="h-5 w-5" />, characterLimit: 500 },
  { id: 'bluesky', name: 'Bluesky', icon: <Cloud className="h-5 w-5" />, characterLimit: 300 },
  { id: 'googlebusiness', name: 'Google Business', icon: <MapPin className="h-5 w-5" /> },
  { id: 'mastodon', name: 'Mastodon', icon: <Globe className="h-5 w-5" />, characterLimit: 500 },
  { id: 'pinterest', name: 'Pinterest', icon: <PinIcon className="h-5 w-5" /> },
  { id: 'threads', name: 'Threads', icon: <Camera className="h-5 w-5" />, characterLimit: 500 }
];

const PostScheduler: React.FC = () => {
  const [post, setPost] = useState({
    content: '',
    scheduledTime: '',
    media: [] as MediaFile[],
    hashtags: [] as string[],
    platforms: {
      facebook: false,
      twitter: false,
      instagram: false,
      linkedin: false,
      youtube: false,
      tiktok: false,
      rumble: false,
      truthsocial: false,
      bluesky: false,
      googlebusiness: false,
      mastodon: false,
      pinterest: false,
      threads: false
    }
  });

  const [hashtagInput, setHashtagInput] = useState('');
  const [showHashtagSuggestions, setShowHashtagSuggestions] = useState(false);
  const [scheduledPosts, setScheduledPosts] = useState<any[]>([]);

  const suggestedHashtags = [
    'FinancialPlanning',
    'WealthManagement',
    'RetirementPlanning',
    'TaxPlanning',
    'EstatePlanning',
    'Insurance',
    'SocialSecurity',
    'EconomicUpdates',
    'PersonalFinance',
    'BusinessNews'
  ];

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newMedia = acceptedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      type: file.type.startsWith('image/') ? 'image' : 'video'
    }));

    setPost(prev => ({
      ...prev,
      media: [...prev.media, ...newMedia]
    }));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/gif': [],
      'video/mp4': []
    },
    maxSize: 10485760,
    maxFiles: 4
  });

  const removeMedia = (index: number) => {
    setPost(prev => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index)
    }));
  };

  const addHashtag = (tag: string) => {
    const formattedTag = tag.startsWith('#') ? tag : `#${tag}`;
    if (!post.hashtags.includes(formattedTag)) {
      setPost(prev => ({
        ...prev,
        hashtags: [...prev.hashtags, formattedTag]
      }));
    }
    setHashtagInput('');
    setShowHashtagSuggestions(false);
  };

  const removeHashtag = (tag: string) => {
    setPost(prev => ({
      ...prev,
      hashtags: prev.hashtags.filter(t => t !== tag)
    }));
  };

  const handleSchedulePost = async () => {
    try {
      const newPost = {
        ...post,
        id: Date.now(),
        status: 'scheduled'
      };
      setScheduledPosts(prev => [...prev, newPost]);
      
      setPost({
        content: '',
        scheduledTime: '',
        media: [],
        hashtags: [],
        platforms: {
          facebook: false,
          twitter: false,
          linkedin: false,
          instagram: false,
          youtube: false,
          tiktok: false,
          rumble: false,
          truthsocial: false,
          bluesky: false,
          googlebusiness: false,
          mastodon: false,
          pinterest: false,
          threads: false
        }
      });

      toast.success('Post scheduled successfully!');
    } catch (error) {
      toast.error('Failed to schedule post');
      console.error('Error scheduling post:', error);
    }
  };

  const handleNewsSelection = (newsItem: NewsItem) => {
    const newsContent = `${newsItem.title}\n\n${newsItem.description}\n\nRead more: ${newsItem.link}`;
    setPost(prev => ({
      ...prev,
      content: prev.content ? `${prev.content}\n\n${newsContent}` : newsContent
    }));
    toast.success('News story added to post');
  };

  return (
    <div className="space-y-8">
      {/* Create Post Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-6">Create Post</h2>
        
        <div className="mb-6">
          <textarea
            className="w-full p-4 border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
            rows={4}
            placeholder="What would you like to share?"
            value={post.content}
            onChange={(e) => setPost({ ...post, content: e.target.value })}
          />
        </div>

        <div className="mb-6">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input {...getInputProps()} />
            <Image className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-gray-600">
              {isDragActive
                ? 'Drop files here...'
                : 'Drag & drop files here, or click to select files'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Supports: JPG, PNG, GIF, MP4 (max 10MB)
            </p>
          </div>

          {post.media.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {post.media.map((media, index) => (
                <div key={index} className="relative rounded-lg overflow-hidden">
                  {media.type === 'image' ? (
                    <img
                      src={media.preview}
                      alt="Preview"
                      className="w-full h-32 object-cover"
                    />
                  ) : (
                    <video
                      src={media.preview}
                      className="w-full h-32 object-cover"
                      controls
                    />
                  )}
                  <button
                    onClick={() => removeMedia(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mb-6">
          <div className="relative">
            <div className="flex items-center border rounded-lg p-2 mb-2">
              <Hash className="w-5 h-5 text-gray-400 mr-2" />
              <input
                type="text"
                value={hashtagInput}
                onChange={(e) => {
                  setHashtagInput(e.target.value);
                  setShowHashtagSuggestions(true);
                }}
                placeholder="Add hashtags..."
                className="flex-1 outline-none"
              />
            </div>

            {showHashtagSuggestions && (
              <div className="absolute z-10 w-full bg-white border rounded-lg shadow-lg mt-1">
                {suggestedHashtags
                  .filter(tag => tag.toLowerCase().includes(hashtagInput.toLowerCase()))
                  .map((tag, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => addHashtag(tag)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50"
                    >
                      #{tag}
                    </button>
                  ))}
              </div>
            )}
          </div>

          {post.hashtags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {post.hashtags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center bg-primary/10 text-primary rounded-full px-3 py-1"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeHashtag(tag)}
                    className="ml-2 text-primary hover:text-primary-dark"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Select Platforms</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {platforms.map((platform) => (
              <label 
                key={platform.id} 
                className="flex items-center gap-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={post.platforms[platform.id as keyof typeof post.platforms]}
                  onChange={(e) => setPost({
                    ...post,
                    platforms: {
                      ...post.platforms,
                      [platform.id]: e.target.checked
                    }
                  })}
                  className="rounded text-primary focus:ring-primary"
                />
                <span className="flex items-center gap-2">
                  {platform.icon}
                  <span className="text-sm font-medium">{platform.name}</span>
                </span>
                {platform.characterLimit && (
                  <span className="ml-auto text-xs text-gray-500">
                    {post.content.length}/{platform.characterLimit}
                  </span>
                )}
              </label>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Schedule Post</h3>
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="datetime-local"
                value={post.scheduledTime}
                onChange={(e) => setPost({ ...post, scheduledTime: e.target.value })}
                className="pl-10 w-full border rounded-lg py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <button
              onClick={handleSchedulePost}
              className="btn btn-primary"
            >
              Schedule Post
            </button>
            <button
              onClick={() => {
                handleSchedulePost();
              }}
              className="btn btn-outline"
            >
              Post Now
            </button>
          </div>
        </div>
      </div>

      {/* News Feed Section */}
      <NewsFeed onSelectNews={handleNewsSelection} />

      {/* Scheduled Posts Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-6">Scheduled Posts</h2>
        <div className="space-y-4">
          {scheduledPosts.map((scheduledPost) => (
            <div
              key={scheduledPost.id}
              className="border rounded-lg p-4 hover:border-primary transition-colors"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {format(new Date(scheduledPost.scheduledTime), 'MMM d, yyyy h:mm a')}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button className="text-gray-400 hover:text-gray-600">
                    <RefreshCw className="h-5 w-5" />
                  </button>
                  <button className="text-gray-400 hover:text-red-500">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <p className="text-gray-900 mb-2">{scheduledPost.content}</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(scheduledPost.platforms)
                  .filter(([_, isSelected]) => isSelected)
                  .map(([platform]) => {
                    const platformInfo = platforms.find(p => p.id === platform);
                    return platformInfo ? (
                      <span
                        key={platform}
                        className="inline-flex items-center text-sm text-gray-600"
                      >
                        {platformInfo.icon}
                        <span className="ml-1">{platformInfo.name}</span>
                      </span>
                    ) : null;
                  })}
              </div>
            </div>
          ))}
          {scheduledPosts.length === 0 && (
            <p className="text-center text-gray-500">No scheduled posts</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostScheduler;